import { Box, Flex } from "@chakra-ui/react";
import { Location } from "components/FormInputs/Location";
import UserSelectDropDown from "components/RFQ/UserSelectDropDown/UserSelectDropDown";
import { FormikContextType, useFormikContext } from "formik";
import { useMemo } from "react";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel";
import { FormikRichTextEditor } from "newui/components/ui/RichTextEditor";
import Heading from "newui/components/ui/Heading";
import Tooltip from "newui/components/Tooltip";
import { Rfx, RfxType } from "newui/features/Rfx/types/rfx.types";
import { SubmissionDeadlineTimePicker } from "./SubmissionDeadlineTimePicker";

interface ProjectDetailProps {
	rfxData?: Rfx;
	rfxTypes: RfxType[];
	subTitlePage?: string;
	titlePage: string;
	onRfxTypeChange: (e: { value: string } | null) => void;
}

export const ProjectDetail = ({
	onRfxTypeChange,
	titlePage,
	subTitlePage,
	rfxTypes,
	rfxData,
}: ProjectDetailProps) => {
	// Infer correct type for Formik context
	const { setFieldValue } = useFormikContext<
		Record<string, unknown>
	>() as FormikContextType<Record<string, unknown>>;

	const isOnboardingProject = rfxData?.project?.is_onboarding;

	const typeOptions = useMemo(() => {
		return rfxTypes
			?.filter((item) =>
				// filter out the onboarding type if not onboarding project
				isOnboardingProject ? true : item.code.toLowerCase() !== "onb"
			)
			.sort((a, b) => a.sort_order - b.sort_order)
			.map((type) => ({
				label: type.name,
				value: type.code,
			}));
	}, [rfxTypes, isOnboardingProject]);

	// The Type select drives local RFx state (quote type, rfxType) in addition
	// to the Formik value, so intercept the write to keep both in sync.
	const handleTypeChange = (field: string, value: string) => {
		setFieldValue(field, value);
		onRfxTypeChange(value ? { value } : null);
	};

	return (
		<div className="flex w-full flex-col">
			<div
				id="subheader-page"
				className="py-8"
			>
				<div
					id="subheader-page-title"
					className="text-xl font-semibold text-primary-900"
				>
					<Heading
						fontSize="36px"
						color="#003CE9"
					>
						{titlePage}
					</Heading>
				</div>
				{subTitlePage && (
					<div
						id="subheader-page-subtitle"
						className="mt-1 font-roboto text-sm text-tertiary-600"
					>
						{subTitlePage}
					</div>
				)}
			</div>

			<div
				id="project-detail-content"
				className="relative h-full min-h-[60vh]"
			>
				<Flex
					gap={{ base: 0, md: 8 }}
					direction={{ base: "column", md: "row" }}
					className="items-baseline mb-6"
				>
					<FormikInputWithLabel
						label="Request Title"
						placeholder="Eg. Request for Lead Design and Engineering Services"
						name="name"
						required
						maxLength={255}
						className="w-full"
					/>
					<FormikInputWithLabel
						label="RFx ID Number"
						placeholder="RFx123"
						name="rfq_id_number"
						className="w-full"
						maxLength={50}
					/>
				</Flex>

				<Flex
					gap={{ base: 0, md: 8 }}
					direction={{ base: "column", md: "row" }}
					className="items-start mb-6"
				>
					<Tooltip
						shouldWrapChildren={false}
						placement="top"
						label={
							isOnboardingProject
								? "This was created as an Onboard & Pre-qualify request and cannot be changed."
								: ""
						}
					>
						<Box
							display="block"
							w="full"
						>
							<FormikInputWithLabel
								label="Type of Request"
								placeholder="Select type"
								name="type"
								type="select"
								options={typeOptions}
								required
								testId="rfx_type_select"
								disabled={isOnboardingProject}
								setFieldValue={handleTypeChange}
							/>
						</Box>
					</Tooltip>
					{
						// @ts-expect-error legacy JS component (prop-types) — listed props are optional at runtime
						<Location
							label="Project Location"
							name="address"
							errorBottom="-3px"
							rfq
							rfqIcon="search-sm"
							className="relative"
							required
							allowInternational
							inputTestId="project_location"
							dropdownTestId="location_auto_complete"
						/>
					}
				</Flex>

				<Flex
					gap={8}
					className="items-baseline pb-4"
				>
					{
						// @ts-expect-error legacy JS component (prop-types) — placeholder prop is optional at runtime
						<UserSelectDropDown
							label="RFx Lead"
							name="rfx_lead_id"
							rfx={rfxData}
							description={`The RFx Lead manages this RFx, including receiving notifications, updates, and responding to Bidder queries.
The RFx Lead email address and phone number will also be displayed on the RFx documents shared to all participants.`}
						/>
					}
				</Flex>

				<Flex>
					<FormikRichTextEditor
						label="Project / Asset Description"
						name="description"
						maxLength={3000}
						showCharacterCount
						className="w-full"
						placeholder="Enter a description..."
						subTitle="Briefly describe the project or asset. Consider the scale, size, quantity or staging and any other important features."
					/>
				</Flex>

				<Flex gap={8}>
					<FormikInputWithLabel
						label="Client"
						placeholder="Enter full name"
						name="client"
						required
						className="mb-6 w-full"
					/>
				</Flex>

				<Flex
					gap={{ base: 0, md: 8 }}
					direction={{ base: "column", md: "row" }}
					className="items-start"
				>
					<FormikInputWithLabel
						type="date"
						label="Submission Deadline"
						name="deadline_date"
						minDate={new Date()}
						className="!font-regular flex-3 w-[80%] !border-fg-disabled_subtle"
						required
					/>
					<div className="w-full md:w-auto">
						<SubmissionDeadlineTimePicker
							className="w-full flex-1"
							name="deadline_time"
							nameTimezone="submission_deadline_timezone"
							label="Submission Time"
						/>
					</div>
				</Flex>

				<Flex>
					<FormikRichTextEditor
						label="Requested Services"
						name="escope_of_service"
						maxLength={5000}
						showCharacterCount
						className="w-full"
						placeholder="Enter a description..."
						subTitle="Provide a brief description of the scope of services needed for this project."
					/>
				</Flex>
			</div>
		</div>
	);
};
